/** @typedef {'android' | 'ios' | 'scan' | 'backup'} DownloadChannelKind */
/** @typedef {'apk' | 'pwa'} AndroidDownloadDestination */
/** @typedef {'pwa' | 'play'} BackupDownloadDestination */
/** @typedef {'pwa' | 'play' | 'chooser'} BackupScanDestination */

/**
 * 顶栏 / H5 频道行 App 入口：iOS 直达 PWA 安装页，其余进下载页。
 *
 * @param {{ isIos: boolean }} input
 * @returns {string}
 */
export function resolveNavAppDownloadPath({ isIos }) {
  return isIos ? '/pwa' : '/download';
}

/**
 * PC 展示四入口选择器，选中项在下方出一张预览码；H5 只保留当前系统能用的入口。
 *
 * @param {{ isMobile: boolean, isIos: boolean, isAndroid: boolean }} input
 * @returns {DownloadChannelKind[]}
 */
export function resolveDownloadChannels({ isMobile, isIos, isAndroid }) {
  if (!isMobile) return ['android', 'ios', 'scan', 'backup'];
  if (isIos) return ['ios'];
  if (isAndroid) return ['android', 'backup'];
  return ['android', 'ios'];
}

/**
 * PC 且已配置 APK 才直下安装包；H5、或 `VUE_APP_ANDROID_APK_URL` 仍为空时进 PWA。
 *
 * @param {{ isMobile: boolean, hasApk: boolean }} input
 * @returns {AndroidDownloadDestination}
 */
export function resolveAndroidDestination({ isMobile, hasApk }) {
  if (!isMobile && hasApk) return 'apk';
  return 'pwa';
}

/**
 * H5 点备用：iPhone 进 PWA，其余进 Google Play。
 *
 * @param {{ isIos: boolean }} input
 * @returns {BackupDownloadDestination}
 */
export function resolveBackupDestination({ isIos }) {
  return isIos ? 'pwa' : 'play';
}

/**
 * 备用短链被打开后：按扫码设备跳转；桌面不按 UA 误跳商店。
 *
 * @param {{ isIos: boolean, isAndroid: boolean }} input
 * @returns {BackupScanDestination}
 */
export function resolveBackupScanDestination({ isIos, isAndroid }) {
  if (isIos) return 'pwa';
  if (isAndroid) return 'play';
  return 'chooser';
}

/**
 * @param {unknown} raw
 * @returns {'' | 'android' | 'ios'}
 */
export function parseDownloadGoOs(raw) {
  const value = Array.isArray(raw) ? raw[0] : raw;
  const os = String(value || '')
    .trim()
    .toLowerCase();
  if (os === 'android' || os === 'ios') return os;
  return '';
}

/**
 * 系统专属码：对端手机扫码视为无效。
 *
 * @param {{ requiredOs: 'android' | 'ios', isIos: boolean, isAndroid: boolean }} input
 * @returns {'pwa' | 'mismatch'}
 */
export function resolvePlatformQrGate({ requiredOs, isIos, isAndroid }) {
  if (requiredOs === 'ios') return isIos ? 'pwa' : 'mismatch';
  return isAndroid && !isIos ? 'pwa' : 'mismatch';
}

/**
 * @param {string} hostname
 */
export function isLoopbackHostname(hostname) {
  const host = String(hostname || '')
    .replace(/^\[|\]$/g, '')
    .toLowerCase();
  return host === 'localhost' || host === '127.0.0.1' || host === '::1';
}

/**
 * 扫码 origin：页面若是 localhost，换成开发机局域网 IP，手机才能打开。
 *
 * @param {string} [locationOrigin]
 * @param {string} [lanHost] 仅主机名，如 192.168.1.8
 */
export function resolveQrPageOrigin(locationOrigin = '', lanHost = '') {
  const origin = typeof locationOrigin === 'string' ? locationOrigin.trim() : '';
  const lan = typeof lanHost === 'string' ? lanHost.trim() : '';
  if (!origin) return '';

  let url;
  try {
    url = new URL(/^[a-z][a-z\d+\-.]*:/i.test(origin) ? origin : `http://${origin}`);
  } catch {
    return origin.replace(/\/$/, '');
  }

  let nextHost = lan;
  try {
    if (/^https?:\/\//i.test(lan)) nextHost = new URL(lan).hostname;
  } catch {
    nextHost = lan;
  }

  if (!nextHost || isLoopbackHostname(nextHost) || !isLoopbackHostname(url.hostname)) {
    return url.origin;
  }

  url.hostname = nextHost;
  return url.origin;
}
